import { Inject, Injectable, OnDestroy } from '@angular/core';
import mixpanel from "mixpanel-browser";
import {environment} from "../../../environments/environment";
import { ContractModel } from 'src/app/global/contract-filter-model';
import { UserService } from './user.service';
import { MIXPANEL_CONFIG, MixPanelConfig } from 'src/app/config/AppInitializer.factory';
import { Subscription } from 'rxjs';
import { FiltersStore } from '../stores/filters.store';

@Injectable({
  providedIn: 'root'
})
export class MixpanelService implements OnDestroy {
  startTime: number = 0;
  totalActiveTime: number = 0;
  isActive: boolean = false;

  oneCustomerName: string = '';
  oneCustomerNameObservable: Subscription | undefined;

  constructor(
    private userService: UserService,
    private filtersStore: FiltersStore,
    @Inject(MIXPANEL_CONFIG) private mixpanelConfig: MixPanelConfig) {
    this.init();
  }


  init(): void {
    // TODO: Silently fail until we have a review how to handle mixpanel for the UI without exposing the token in client side code. The backend will send an empty token for now.
    if (!this.mixpanelConfig.token) {
      return;
    }
    mixpanel.init(this.mixpanelConfig.token,
      {
        record_sessions_percent: this.mixpanelConfig?.sessionReplayCaptureRate ?? 100, // Enables Session Replay, recording x% of all sessions
        record_mask_text_selector: '', //unmask all text elements
        record_block_selector: '', //unmask images and videos
        autocapture: this.mixpanelConfig?.sessionReplayCaptureRate > 0 ? {
          pageview: "full-url",
          click: true,
          dead_click: false,
          input: false,
          rage_click: false,
          scroll: false,
          submit: false,
          capture_text_content: false,
        } : false
      }
    );
    this.addVisibilityChangeListener();
    this.oneCustomerNameObservable = this.filtersStore.oneCustomerNameObservable.subscribe((f: string) =>
        this.oneCustomerName = f
      );
  }

  ngOnDestroy(): void {
    this.oneCustomerNameObservable?.unsubscribe();
  }

  identify(): void {
    mixpanel.identify(this.userService.getUsername()!.toLowerCase());
    const firstName = this.userService.getFirstName();
    const lastName = this.userService.getLastName();
    const name = firstName + ' ' + lastName;
    mixpanel.people.set({
      first_name: firstName,
      last_name: lastName,
      name: name,
    });
    mixpanel.register({
      name: name,
      environment: environment.name,
    });
  }

  track(eventName: string, properties: any = {}): void {
    // TODO : Check with business about deprecating obsolete/duplicate mixpanel events.
    // Most events have the same selected filter properties in payload.
    // They can be consolidated in the store of having same logic repeated across multiple components.
    const inferredH5Payload = this.getInferredH5Payload(properties);
    const completeProperties = this.populateMissingProperties(properties);
    properties = {
      ...completeProperties,
      ...inferredH5Payload,
      "Entry Source": this.oneCustomerName.length ? "One Customer entry point" : "Direct EBR URL entry point",
      ...(this.oneCustomerName.length && {
        "Customer Name": this.oneCustomerName
      }),
    };

    // TODO: Silently fail if token is not defined. If not handled explicitly the mixpanel browser 
    // library will queue the events and silently drop them when the token is not defined and the service is not initialized.
    if (this.mixpanelConfig.token) {
      mixpanel.track(eventName, properties);
    }
  }

  private getInferredH5Payload(properties: any): any {
    // Check if caller provided H5 or H7 values.
    // Treat an explicitly provided empty array as "provided" (caller intent),
    // so check for property presence rather than truthy length.
    const hasH5 = properties && Object.prototype.hasOwnProperty.call(properties, 'H5s');
    const hasH7 = properties && Object.prototype.hasOwnProperty.call(properties, 'H7s');

    // If caller provided both, don't augment
    if (hasH5 && hasH7) {
      return {};
    }

    // For all cases where H5 or H7 is missing, consult the store
    const selectedFilters = this.filtersStore.selectedFiltersValue;
    if (!selectedFilters) {
      // No store data — fill missing side with N/A
      if (hasH7 && !hasH5) return { H5s: ['N/A'] };
      if (hasH5 && !hasH7) return { H7s: ['N/A'] };
      return {};
    }

    const isContractLikeDivision =
      selectedFilters.division === 'QSR' ||
      selectedFilters.division === 'FRS' ||
      selectedFilters.division === 'Pest' ||
      (selectedFilters.division === 'Institutional' && selectedFilters.dataView === 'Contract');

    if (!isContractLikeDivision) {
      return {};
    }

    const selectedH5 = selectedFilters.topLevelContractGroup?.filter(Boolean) ?? [];
    const selectedH7 = selectedFilters.secondLevelContractFilter?.filter(Boolean) ?? [];
    const inferredH5 = selectedFilters.inferredH5?.filter(Boolean) ?? [];
    const h5Payload = selectedH5.length > 0 ? selectedH5 : (inferredH5.length > 0 ? inferredH5 : ['N/A']);
    const h7Payload = selectedH7.length > 0 ? selectedH7 : ['N/A'];

    // Caller provided H7 but not H5 — fill H5 from store (inferred or N/A), keep caller's H7
    if (hasH7 && !hasH5) {
      return { H5s: h5Payload };
    }

    // Caller provided H5 but not H7 — fill H7 from store (or N/A), keep caller's H5/s
    if (hasH5 && !hasH7) {
      return { H7s: h7Payload };
    }

    // Neither provided — return both from store
    if (selectedH5.length === 0 && selectedH7.length === 0) {
      return { H5s: ['N/A'], H7s: ['N/A'] };
    }

    if (selectedH5.length > 0 && selectedH7.length === 0) {
      return { H5s: selectedH5, H7s: ['N/A'] };
    }

    if (selectedH5.length === 0 && selectedH7.length > 0) {
      return { H5s: h5Payload, H7s: selectedH7 };
    }

    // Both H5/s and H7/s in store
    return { H5s: selectedH5, H7s: selectedH7 };
  }

  private populateMissingProperties(properties: any): any {
    const hasAll =
    !!properties &&
    !!properties.Period &&
    !!properties.Region &&
    (!!properties.Countries || !!properties.Country) &&
    (!!properties['Custom Date Range'] || !!properties.CustomDate || !!properties.CustomDateRange);
      
    if (hasAll) 
      return properties;
    
      const selectedFilters = this.filtersStore.selectedFiltersValue;
    
    return {
    Period: selectedFilters?.period ?? '',
    Region: selectedFilters?.regions ?? '',
    Countries: selectedFilters?.countries ?? (selectedFilters?.countries ? [selectedFilters.countries] : ['N/A']),
    'Custom Date Range': selectedFilters?.dateRange ?? 'N/A',
    'Accounts': selectedFilters?.accts ?? [],
    ...properties
    };
  }


  time_event(eventName: string) {
    mixpanel.time_event(eventName);
  }

  /**
   *  Start tracking active duration of user
   */
  startTrackingActiveTime(): void {
    this.isActive = true;
    this.startTime = Date.now();
  }

  /**
   * Stop tracking active duration of user
   */
  stopTrackingActiveTime(): void {
    if (this.isActive) {
      let endTime = Date.now();
      let duration = endTime - this.startTime;
      this.totalActiveTime += duration;
      this.isActive = false;
    }
  }

  /**
   * Adds an event listener to start/stop tracking active duration when the website isn't visible (and therefore not active).
   */
  addVisibilityChangeListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.startTrackingActiveTime();
      } else {
        this.stopTrackingActiveTime();
      }
    });
  }

  /**
   * Prepares active duration tracking for a new component
   */
  initializeActiveDurationTracking(): void {
    this.totalActiveTime = 0; // Reset the total active time
    this.startTime = Date.now(); // Start tracking active time
    this.isActive = true; // Set the active state
  }

  /**
   * Tracks user leaving a page, sending the page name and user's active duration on that page
   * @param pageName The page that has been viewed
   */
  trackPageView(pageName: string): void {
    let eventName = `${pageName} Viewed`;
    let props ={
      "Page Name": pageName,
      "Active Duration": `${Math.round(this.totalActiveTime / 1000)} seconds`
    };
    this.track(eventName, props);
  }

  // TODO: Is this implementation needed? Its only used in TVD and the same logic can be implemented
  // in the component itself without needing to pass pageName as a parameter. Consider removing if not necessary.
  /**
   * Generic method which directs the logic to a method that tracks filters specfic to selected division/dataview
   * @param eventName Name of the mixpanel event to track
   * @param filters Applied filters
   * @param pageName Page name of where the event occured
   */
  trackEventWithFilters(eventName: string, filters: ContractModel | undefined, pageName: string, additionalProps?: any): void {
    if(filters?.division == "Distributor") {
        this.trackEventWithDistributorFilters(eventName, filters, pageName, additionalProps);
    } else {
      this.trackEventWithContractFilters(eventName, filters!, pageName, additionalProps);
    }
  }

  /**
   * Tracks a mixpanel event with filters specific to Contract dataview or QSR division
   * @param eventName Name of the mixpanel event to track
   * @param filters Applied filters
   * @param pageName Page name of where the event occured
   */
  private trackEventWithContractFilters(eventName: string, filters: ContractModel, pageName: string, additionalProps?: any) {
    let props = {
      'Division': filters.division,
      'DataView': filters.dataView,
      'Region': filters.regions,
      'Countries': filters.countries,
      'H5s': filters.topLevelContractGroup?.length ? filters.topLevelContractGroup : filters.inferredH5,
      'H7s': filters.secondLevelContractFilter,
      'Accounts': filters.accts,
      'Period': filters.period,
      "Custom Date Range": filters?.dateRange,
      'Template': filters.presentationType,
      'Translation': filters.translation,
      'Page Name': pageName,
      ...additionalProps
    }

    this.track(eventName, props);
  }

  /**
   * Tracks a mixpanel event with filters specific to Distributor division
   * @param eventName Name of the mixpanel event to track
   * @param filters Applied filters
   * @param pageName Page name of where the event occured
   */
  private trackEventWithDistributorFilters(eventName: string, filters: ContractModel, pageName: string, additionalProps?: any) {
    let props = {
      'Division': filters.division,
      'DataView': filters.dataView,
      'Partner Groups': filters.topLevelContractGroup,
      'Distributor Groups': filters.secondLevelContractFilter,
      'Accounts': filters.accts,
      'Period': filters.period,
      "Custom Date Range": filters?.dateRange,
      'Page Name': pageName,
      ...additionalProps
    };
    this.track(eventName, props);
  }


}
